// The DFT module's defect levels (D-2): after a run of a defect supercell, its localised states near the gap -
// energy from the host's valence top, occupation, spin, how much of each sits on the defect and its neighbours, the
// orbital character - in a level diagram against the host's band edges (of the host reference run, or else the defect
// cell's own bulk-like bands), and the magnetic moments on the defect.
import { Alert, Badge, Group, List, NumberInput, Select, Stack, Table, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { Plot } from "../../components/Plot";
import { qeDefectLevels, type DefectLevels } from "../../lib/qe";

const GRID = "#e9e9e6";
const FILLED = "#2a78d6", EMPTY = "#eb6834", PARTIAL = "#8b5cf6", BULK = "#b8b6af";
const fmt = (x?: number) => (x == null ? "?" : `${x >= 0 ? "+" : ""}${x.toFixed(2)}`);
const CH: Record<string, string> = { all: "", up: "spin ↑", down: "spin ↓" };

/** How full a level is, in words: filled, empty, or the electrons it holds. */
export function occupationText(occ: number | null, electrons: number | null): string {
  if (occ == null) return "?";
  if (occ >= 0.95) return "filled";
  if (occ <= 0.05) return "empty";
  return `${(electrons ?? occ).toFixed(2)} e`;
}
const colorOf = (occ: number | null) => (occ == null ? PARTIAL : occ >= 0.95 ? FILLED : occ <= 0.05 ? EMPTY : PARTIAL);

/** The level diagram as a Plotly figure: one column per spin channel, the host's bands shaded, the defect cell's
 * bands as short lines (localised: thick, coloured by occupation; the rest grey), the Fermi level dashed. */
export function levelFigure(d: DefectLevels, window = 2) {
  const chans = Object.keys(d.states);
  const ymin = -window, ymax = d.gap_eV + window;
  const shapes: any[] = [];
  const data: any[] = [];
  const x0 = -0.5, x1 = chans.length - 0.5;
  shapes.push({ type: "rect", xref: "x", yref: "y", x0, x1, y0: ymin, y1: 0, fillcolor: "rgba(42,120,214,0.10)", line: { width: 0 }, layer: "below" });
  shapes.push({ type: "rect", xref: "x", yref: "y", x0, x1, y0: d.gap_eV, y1: ymax, fillcolor: "rgba(235,104,52,0.10)", line: { width: 0 }, layer: "below" });
  chans.forEach((ch, c) => {
    const hx: number[] = [], hy: number[] = [], ht: string[] = [];
    for (const r of d.states[ch]) {
      if (r.localised) {
        shapes.push({ type: "rect", xref: "x", yref: "y", x0: c - 0.3, x1: c + 0.3, y0: r.min_rel_eV, y1: r.max_rel_eV,
          fillcolor: "rgba(139,92,246,0.12)", line: { width: 0 } });
      }
      shapes.push({ type: "line", xref: "x", yref: "y", x0: c - (r.localised ? 0.3 : 0.22), x1: c + (r.localised ? 0.3 : 0.22),
        y0: r.energy_eV, y1: r.energy_eV,
        line: { color: r.localised ? colorOf(r.occupation) : BULK, width: r.localised ? 3 : 1,
          dash: r.localised && r.occupation != null && r.occupation <= 0.05 ? "dash" : "solid" } });
      if (r.localised) {
        hx.push(c); hy.push(r.energy_eV);
        ht.push(`band ${r.band}${CH[ch] ? ` (${CH[ch]})` : ""}<br>E − VBM = ${r.energy_eV.toFixed(3)} eV<br>` +
          `${occupationText(r.occupation, null)} · ${Math.round((r.localisation ?? 0) * 100)} % on the defect`);
      }
    }
    data.push({ x: hx, y: hy, type: "scatter", mode: "markers", marker: { size: 14, opacity: 0 }, hovertext: ht, hoverinfo: "text",
      showlegend: false, name: ch });
    const f = ch === "up" ? d.fermi.fermi_up ?? d.fermi.fermi : ch === "down" ? d.fermi.fermi_down ?? d.fermi.fermi : d.fermi.fermi;
    if (f != null)
      shapes.push({ type: "line", xref: "x", yref: "y", x0: c - 0.45, x1: c + 0.45, y0: f, y1: f, line: { color: "#898781", dash: "dot", width: 1 } });
  });
  const layout = {
    xaxis: { range: [x0, x1], tickvals: chans.map((_, i) => i), ticktext: chans.map((c) => CH[c] || "states"), zeroline: false,
      showgrid: false, fixedrange: true },
    yaxis: { title: { text: "E − E<sub>VBM</sub> of the host (eV)" }, range: [ymin, ymax], gridcolor: GRID, zeroline: false },
    shapes, margin: { l: 60, r: 20, t: 20, b: 40 }, hovermode: "closest",
    annotations: [
      { x: x1, y: ymin + 0.05 * window, xanchor: "right", yanchor: "bottom", text: "valence band", showarrow: false, font: { size: 10, color: "#2a78d6" } },
      { x: x1, y: ymax - 0.05 * window, xanchor: "right", yanchor: "top", text: "conduction band", showarrow: false, font: { size: 10, color: "#eb6834" } }],
  };
  return { data, layout };
}

export function DefectLevelsView({ jobId }: { jobId: string }) {
  const [host, setHost] = useState<string | null>(null);
  const [window, setWindow] = useState(2);
  const q = useQuery({ queryKey: ["qe-defect-levels", jobId, host, window], queryFn: () => qeDefectLevels(jobId, host),
    staleTime: 60_000, retry: false });
  const d = q.data;
  const fig = useMemo(() => (d ? levelFigure(d, window) : null), [d, window]);
  const hostOptions = [{ value: "none", label: "the defect cell's own bands" },
    ...(d?.host_candidates ?? []).map((c) => ({ value: c.job_id, label: c.name ?? c.job_id }))];
  const m = d?.moments ?? {};
  return (
    <Stack gap="xs" data-testid="defect-levels">
      <ErrorBox error={q.error} />
      {d && <>
        <Group gap={6}>
          <Text size="sm" fw={600}>Defect {d.label}</Text>
          <Badge variant="light" color={d.in_gap ? "teal" : "gray"}>{d.in_gap} level{d.in_gap === 1 ? "" : "s"} in the gap</Badge>
          <Badge variant="light" color="gray">host gap {d.gap_eV.toFixed(2)} eV (along the path)</Badge>
          {d.spin && <Badge variant="light" color="grape">spin-polarised</Badge>}
        </Group>
        <Group gap="xs" align="flex-end">
          <Select size="xs" w={300} label="Band edges from" allowDeselect={false} data={hostOptions}
            value={host ?? d.host_job ?? "none"} onChange={(v) => setHost(v ?? "none")} data-testid="levels-host" />
          <NumberInput size="xs" w={140} label="Show bands within (eV)" value={window} min={0.5} max={6} step={0.5}
            onChange={(v) => typeof v === "number" && v >= 0.5 && v <= 6 && setWindow(v)} />
          <Text size="xs" c="dimmed">{d.edges_from === "host reference"
            ? `aligned on ${d.alignment?.method ?? "the valence band bottom"} (shift ${d.shift_eV >= 0 ? "+" : ""}${d.shift_eV.toFixed(3)} eV` +
              (d.alignment?.edge_check_eV?.cbm_eV != null ? `; the cell's own edges ${fmt(d.alignment.edge_check_eV.vbm_eV)} / ${fmt(d.alignment.edge_check_eV.cbm_eV)} eV from the host's` : "") + ")"
            : "make and run the host reference (Defects tab) for the host's own edges"}</Text>
        </Group>
        {d.warnings.length > 0 && <Alert color="orange" p="xs"><List size="xs">{d.warnings.map((w) => <List.Item key={w}>{w}</List.Item>)}</List></Alert>}
        {fig && <Plot data={fig.data} layout={fig.layout} height={440} />}
        <Text size="xs" c="dimmed">Thick lines: states with much of their weight on the defect and its nearest neighbours (blue filled,
          orange dashed empty, violet partly filled; the shaded strip: how much they disperse along the path); thin grey: the other
          bands of the supercell; dotted: the Fermi level. Kohn-Sham levels: the gap of a semi-local functional is too small, and
          the levels' positions are approximate (about 0.1 eV here); charge transition levels come with the formation energies.</Text>
        {d.levels.length > 0 ? (
          <Table fz="xs" verticalSpacing={2} data-testid="levels-table">
            <Table.Thead><Table.Tr><Table.Th>Band</Table.Th><Table.Th>Spin</Table.Th><Table.Th>E − VBM (eV)</Table.Th><Table.Th>Width</Table.Th>
              <Table.Th>Where</Table.Th><Table.Th>Occupation</Table.Th><Table.Th>On the defect</Table.Th><Table.Th>Character</Table.Th>
              <Table.Th>Atoms</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {d.levels.map((l) => (
                <Table.Tr key={`${l.channel}-${l.band}`}>
                  <Table.Td>{l.band}</Table.Td>
                  <Table.Td>{l.channel === "all" ? "–" : l.channel === "up" ? "↑" : "↓"}</Table.Td>
                  <Table.Td>{l.energy_eV.toFixed(3)}</Table.Td>
                  <Table.Td>{l.width_eV.toFixed(3)}</Table.Td>
                  <Table.Td>{l.where}</Table.Td>
                  <Table.Td>{occupationText(l.occupation, l.electrons)}</Table.Td>
                  <Table.Td>{Math.round(l.localisation * 100)} %<Text span size="xs" c="dimmed"> ({Math.round(l.localisation_2 * 100)} % with the next shell)</Text></Table.Td>
                  <Table.Td>{l.character.map((c) => `${c.element}-${c.l} ${Math.round(c.share * 100)} %`).join(", ")}</Table.Td>
                  <Table.Td>{l.atoms.map((a) => `${a.element}${a.atom}`).join(", ")}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : <Text size="sm">No localised state near the gap: the defect's states lie in the bands (or the supercell is too small to tell them apart).</Text>}
        {(m.total != null || m.sites?.length) && <MomentsBlock m={m} />}
        {d.notes.length > 0 && <Text size="xs" c="dimmed">{d.notes.join(" · ")}</Text>}
      </>}
    </Stack>
  );
}

const METHOD: Record<string, string> = {
  lowdin: "Löwdin populations from projwfc.x on the SCF's k-mesh (the usual moment per atom)",
  "band path": "estimated from the band path's projections (this run has no projwfc.x on the SCF: rougher)",
  spheres: "inside pw.x's atomic spheres only (most of the spin between the atoms is not counted)",
};

/** The moments: the total (pw.x), on the defect and its shells, and per atom (Löwdin; pw.x's spheres beside). */
export function MomentsBlock({ m }: { m: DefectLevels["moments"] }) {
  const sign = (x: number) => `${x >= 0 ? "+" : ""}${x.toFixed(2)}`;
  return (
    <Stack gap={2} data-testid="defect-moments">
      <Text size="sm" fw={600}>Magnetic moments</Text>
      <Text size="xs">Total {m.total?.toFixed(2) ?? "?"} μB (absolute {m.absolute?.toFixed(2) ?? "?"} μB)
        {m.region != null ? ` · on the defect and its nearest neighbours ${m.region.toFixed(2)} μB` : ""}
        {m.region_2 != null ? `, with the next shell ${m.region_2.toFixed(2)} μB` : ""}
        {m.per_atom_sum != null ? ` · all atoms ${m.per_atom_sum.toFixed(2)} μB` : ""}</Text>
      {m.method && <Text size="xs" c="dimmed">Per atom: {METHOD[m.method] ?? m.method}.</Text>}
      {!!m.sites?.length && (
        <Table fz="xs" verticalSpacing={1} withRowBorders={false} data-testid="moments-table" maw={460}>
          <Table.Thead><Table.Tr><Table.Th>Atom</Table.Th><Table.Th>Where</Table.Th>
            <Table.Th>Moment (μB)</Table.Th>{m.sites.some((s) => s.sphere != null) && m.method !== "spheres" && <Table.Th>pw.x sphere</Table.Th>}</Table.Tr></Table.Thead>
          <Table.Tbody>
            {m.sites.map((s) => (
              <Table.Tr key={s.atom}><Table.Td>{s.element}{s.atom}</Table.Td><Table.Td>{s.where}</Table.Td><Table.Td>{sign(s.moment)}</Table.Td>
                {s.sphere != null && m.method !== "spheres" && <Table.Td c="dimmed">{sign(s.sphere)}</Table.Td>}</Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
